import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Banner, Button, Card, IconButton, Input } from '@/components/ds-dlp';
import { AuthLayout } from '@/app/AuthLayout';
import { accountApi, passwordProblem } from '../api/account';
import { detectTenantSlugFromHost, getPersistedTenantSlug } from '../auth/tenantSlug';

// The page the emailed link opens: /reset-password?token=…. The token is
// the authorisation; it works once and for 30 minutes. A reset signs out
// every session, so the user signs in again afterwards.
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') ?? '', []);
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const logoSlug = useMemo(() => detectTenantSlugFromHost(hostname) ?? getPersistedTenantSlug(), [hostname]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = passwordProblem(password, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      setDone(await accountApi.resetPassword(token, password));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    }
    setIsLoading(false);
  };

  const toSignIn = () => navigate('/login', { replace: true });

  return (
    <AuthLayout subtitle="Choose a new password" tenantSlug={logoSlug}>
      <Card>
        <Card.Body>
          {!token ? (
            <div className="flex flex-col gap-4">
              <Banner variant="danger">This link has no reset token. Open the link from the email again, or ask for a new one.</Banner>
              <Button onClick={() => navigate('/forgot-password', { replace: true })} className="w-full">
                Ask for a new link
              </Button>
            </div>
          ) : done ? (
            <div className="flex flex-col gap-4">
              <Banner variant="success">{done}</Banner>
              <Button onClick={toSignIn} className="w-full">
                Sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {error ? <Banner variant="danger">{error}</Banner> : null}
              <Input
                id="reset-password"
                label="New password"
                type={show ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                autoFocus
                help="At least 8 characters."
                trailingSlot={
                  <IconButton
                    size="sm"
                    icon={show ? <EyeOff /> : <Eye />}
                    aria-label={show ? 'Hide password' : 'Show password'}
                    tooltip={show ? 'Hide password' : 'Show password'}
                    onClick={() => setShow((v) => !v)}
                  />
                }
              />
              <Input
                id="reset-password-confirm"
                label="Confirm new password"
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
              />
              <Button type="submit" loading={isLoading} disabled={isLoading || !password || !confirm} className="mt-1 w-full">
                Set new password
              </Button>
              {error && /not valid or has expired/.test(error) ? (
                <Button variant="secondary" onClick={() => navigate('/forgot-password', { replace: true })} className="w-full">
                  Ask for a new link
                </Button>
              ) : null}
            </form>
          )}
        </Card.Body>
      </Card>
    </AuthLayout>
  );
};

export default ResetPassword;
